// EditUserDrawer.jsx
import React, { useState, useEffect } from "react";
import { adminApi } from "./adminApi";
import "./EditUserDrawer.css";

function toDateInputValue(iso) {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

function generateRandomPassword() {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let result = "";
  for (let i = 0; i < 10; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
}

function initials(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}

const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const ToggleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="8" width="20" height="8" rx="4" />
    <circle cx="8" cy="12" r="2.4" fill="currentColor" stroke="none" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export default function EditUserDrawer({ user, onClose, onUpdated }) {
  const [username, setUsername] = useState(user.username);
  const [usernameError, setUsernameError] = useState("");
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameSaved, setUsernameSaved] = useState(false);
  const [usernameCopied, setUsernameCopied] = useState(false);

  const [expiryDate, setExpiryDate] = useState(toDateInputValue(user.expiresAt));
  const [expiryError, setExpiryError] = useState("");
  const [expirySaving, setExpirySaving] = useState(false);
  const [expirySaved, setExpirySaved] = useState(false);

  const [statusSaving, setStatusSaving] = useState(false);
  const [isActive, setIsActive] = useState(user.active);

  const [currentPassword, setCurrentPassword] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSaved, setResetSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadPassword() {
      setPasswordLoading(true);
      setPasswordError("");
      try {
        const result = await adminApi.getCurrentPassword(user.id);
        if (!cancelled) setCurrentPassword(result.password);
      } catch (err) {
        if (!cancelled) {
          setPasswordError(
            err.message ||
              "No viewable password stored for this account (created before this feature existed). Set one below."
          );
        }
      } finally {
        if (!cancelled) setPasswordLoading(false);
      }
    }
    loadPassword();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  async function handleSaveUsername() {
    if (username.trim() === user.username) return;
    setUsernameError("");
    setUsernameSaving(true);
    try {
      await adminApi.updateUsername(user.id, username.trim());
      setUsernameSaved(true);
      onUpdated();
      setTimeout(() => setUsernameSaved(false), 2000);
    } catch (err) {
      setUsernameError(err.message || "Could not update username.");
    } finally {
      setUsernameSaving(false);
    }
  }

  async function handleSaveExpiry() {
    if (!expiryDate) return;
    setExpiryError("");
    setExpirySaving(true);
    try {
      await adminApi.setExpiryDate(user.id, `${expiryDate}T00:00:00`);
      setExpirySaved(true);
      onUpdated();
      setTimeout(() => setExpirySaved(false), 2000);
    } catch (err) {
      setExpiryError(err.message || "Could not update expiry.");
    } finally {
      setExpirySaving(false);
    }
  }

  async function handleToggleStatus() {
    setStatusSaving(true);
    try {
      await adminApi.setActive(user.id, !isActive);
      setIsActive(!isActive);
      onUpdated();
    } catch (err) {
      // status errors are rare - leave the toggle as-is on failure
    } finally {
      setStatusSaving(false);
    }
  }

  function handleGenerateRandom() {
    setNewPasswordInput(generateRandomPassword());
    setResetError("");
  }

  async function handleSaveNewPassword() {
    if (!newPasswordInput.trim()) return;
    setResetError("");
    setResetSaving(true);
    try {
      const result = await adminApi.resetPassword(user.id, newPasswordInput.trim());
      setCurrentPassword(result.plaintextPassword);
      setNewPasswordInput("");
      setPasswordError("");
      setResetSaved(true);
      onUpdated();
      setTimeout(() => setResetSaved(false), 2000);
    } catch (err) {
      setResetError(err.message || "Could not save that password.");
    } finally {
      setResetSaving(false);
    }
  }

  function copyUsername() {
    navigator.clipboard.writeText(username);
    setUsernameCopied(true);
    setTimeout(() => setUsernameCopied(false), 2000);
  }

  function copyPassword() {
    if (!currentPassword) return;
    navigator.clipboard.writeText(currentPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  }

  return (
    <div className="eud-overlay" onClick={onClose}>
      <div className="eud-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="eud-header">
          <div className="eud-header-left">
            <div className="eud-avatar">{initials(user.companyName)}</div>
            <div>
              <h2 className="eud-title">{user.companyName}</h2>
              <p className="eud-subtitle">Edit client access</p>
            </div>
          </div>
          <button className="eud-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="eud-body">
          {/* Username */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><UserIcon /></span>
              <span className="eud-label">Username</span>
            </div>
            <div className="eud-inline-row">
              <input
                className="eud-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <button className="eud-btn-icon" onClick={copyUsername} title="Copy username">
                {usernameCopied ? "✓" : "Copy"}
              </button>
              <button
                className="eud-btn-small"
                onClick={handleSaveUsername}
                disabled={usernameSaving || username.trim() === user.username}
              >
                {usernameSaving ? "Saving…" : usernameSaved ? "Saved ✓" : "Save"}
              </button>
            </div>
            {usernameError && <div className="eud-error">{usernameError}</div>}
          </div>

          {/* Current password */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><LockIcon /></span>
              <span className="eud-label">Current Password</span>
            </div>

            {passwordLoading ? (
              <div className="eud-hint">Loading…</div>
            ) : currentPassword ? (
              <div className="eud-inline-row">
                <input className="eud-input eud-mono" value={currentPassword} readOnly />
                <button className="eud-btn-icon" onClick={copyPassword} title="Copy password">
                  {passwordCopied ? "✓" : "Copy"}
                </button>
              </div>
            ) : (
              <div className="eud-warning">{passwordError}</div>
            )}
          </div>

          {/* Set new password */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><LockIcon /></span>
              <span className="eud-label">Set New Password</span>
            </div>

            <div className="eud-inline-row">
              <input
                className="eud-input eud-mono"
                type="text"
                placeholder="Type a new password, or generate one…"
                value={newPasswordInput}
                onChange={(e) => setNewPasswordInput(e.target.value)}
              />
              <button className="eud-btn-icon" onClick={handleGenerateRandom} title="Fill with a random password">
                Generate
              </button>
            </div>
            <button
              className="eud-btn-small eud-btn-full"
              onClick={handleSaveNewPassword}
              disabled={resetSaving || !newPasswordInput.trim()}
              style={{ marginTop: 8 }}
            >
              {resetSaving ? "Saving…" : resetSaved ? "Saved ✓" : "Save Password"}
            </button>
            <p className="eud-hint">
              Nothing changes until you click "Save Password" - generating just fills in a
              suggestion you can edit first.
            </p>
            {resetError && <div className="eud-error">{resetError}</div>}
          </div>

          {/* Status */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><ToggleIcon /></span>
              <span className="eud-label">Status</span>
            </div>
            <div className="eud-inline-row">
              <span className={`eud-status-pill ${isActive ? "eud-status-active" : "eud-status-inactive"}`}>
                {isActive ? "Active" : "Deactivated"}
              </span>
              <button className="eud-btn-small" onClick={handleToggleStatus} disabled={statusSaving}>
                {statusSaving ? "Updating…" : isActive ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          </div>

          {/* Expiry */}
          <div className="eud-field-group">
            <div className="eud-label-row">
              <span className="eud-label-icon"><CalendarIcon /></span>
              <span className="eud-label">Expiry Date</span>
            </div>
            <div className="eud-inline-row">
              <input
                className="eud-input"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
              <button
                className="eud-btn-small"
                onClick={handleSaveExpiry}
                disabled={expirySaving || !expiryDate}
              >
                {expirySaving ? "Saving…" : expirySaved ? "Saved ✓" : "Save"}
              </button>
            </div>
            {expiryError && <div className="eud-error">{expiryError}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}